import logger from '@/utils/client-logger';
import { getEventAdapter } from './adapters/eventAdapters';
import { ProviderRegistry } from './providerRegistry';
import { Provider, ProviderType, TrackingEvent } from './types';
import { generateEventId } from './utils/eventIdGenerator';

// 第三方营销脚本可能被浏览器或网络拦截，出现既不 onload 也不 onerror 的 pending 状态。
// 单个 provider 初始化不能阻塞神策等已就绪 provider 的自定义事件队列。
const PROVIDER_INIT_TIMEOUT_MS = 3000;

type QueuedTrackCall = {
  event: TrackingEvent;
  providers?: ProviderType[];
  adaptionData?: unknown;
  adapterType?: string;
};

type ProviderInitStatus = 'initializing' | 'ready' | 'failed';

export class ReportingCenter {
  private static instance: ReportingCenter;

  static getInstance(): ReportingCenter {
    if (!ReportingCenter.instance) {
      ReportingCenter.instance = new ReportingCenter();
      // 暴露日志控制方法到window对象
      if (typeof window !== 'undefined') {
        (window as any).enableReportCenterLog = ReportingCenter.instance.enableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).disableReportCenterLog = ReportingCenter.instance.disableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).getReportCenterLogStatus = ReportingCenter.instance.getLoggingStatus.bind(
          ReportingCenter.instance,
        );
        (window as any).listReportCenterProviders = ReportingCenter.instance.listProviders.bind(
          ReportingCenter.instance,
        );
      }
    }
    return ReportingCenter.instance;
  }

  private registry: ProviderRegistry;
  private initialized = false;
  private eventQueue: QueuedTrackCall[] = [];
  // 按 provider 隔离队列：慢初始化的第三方不阻塞全局，也不会丢自己的待发事件。
  private providerEventQueues = new Map<ProviderType, TrackingEvent[]>();
  private providerInitStatus = new Map<ProviderType, ProviderInitStatus>();

  private constructor() {
    this.registry = new ProviderRegistry();
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    const providers = this.registry.getEnabledProviders();
    const initPromises = providers.map((provider) => this.initializeProvider(provider));

    await Promise.allSettled(initPromises);

    // Process any events that were queued during initialization
    this.initialized = true;
    this.processQueue();
  }

  registerProvider(provider: Provider): void {
    this.registry.registerProvider(provider);
  }

  /**
   * 追踪事件
   * @param event 事件数据 (可以是原始 TrackingEvent 或业务数据)
   * @param specificProviders 指定的 Provider 类型数组
   * @param adapterType 适配器类型，用于触发对应适配器 (如 'checkout.initiate')
   */
  track(
    event: TrackingEvent,
    specificProviders?: ProviderType[],
    adaptionData?: unknown,
    adapterType?: string,
  ): void {
    const providers = this.resolveTargetProviders(specificProviders);

    // 初始化之前若还没有可解析 provider，保留原始调用，待 provider 注册后再分发。
    if (!this.initialized && providers.length === 0) {
      this.eventQueue.push({ event, providers: specificProviders, adaptionData, adapterType });
      return;
    }

    this.trackResolvedProviders(event, providers, adaptionData, adapterType);
  }

  private trackResolvedProviders(
    event: TrackingEvent,
    providers: Provider[],
    adaptionData?: unknown,
    adapterType?: string,
  ): void {
    // 如果有 adapterType，必须使用适配器
    if (adapterType) {
      const adapter = getEventAdapter(adapterType);
      if (!adapter) {
        logger.error(`❌ No adapter found for adapter type: ${adapterType}`);
        return;
      }

      // 生成统一的事件 ID
      const eventId = generateEventId();
      const shouldMirrorSensorsEvent = providers.some(
        (provider) => provider.type === ProviderType.SENSORS,
      );
      let sensorsAdaptedEvent: TrackingEvent | TrackingEvent[] | null | undefined;
      logger.log(`🎯 Using event adapter for type: ${adapterType}`, {
        originalData: event,
        targetProviders: providers.map((p) => p.type),
        eventId,
      });

      // 为每个 Provider 使用适配器生成适配后的事件
      providers.forEach((provider) => {
        if (!provider.isInitialized()) {
          logger.warn(`⚠️ Provider ${provider.type} is not initialized`);
          return;
        }

        let adaptedEvent: TrackingEvent | TrackingEvent[] | null;
        if (shouldMirrorSensorsEvent && provider.type === ProviderType.SNOWPLOW) {
          // Snowplow 作为神策镜像时复用 Sensors adapter 结果，确保事件名与属性 1:1。
          adaptedEvent =
            sensorsAdaptedEvent === undefined
              ? adapter(adaptionData, ProviderType.SENSORS, eventId)
              : sensorsAdaptedEvent;
        } else {
          adaptedEvent = adapter(adaptionData, provider.type, eventId);
        }
        if (provider.type === ProviderType.SENSORS) {
          sensorsAdaptedEvent = adaptedEvent;
        }
        if (!adaptedEvent) {
          logger.error(
            `❌ Adapter failed to generate event for provider ${provider.type} with adapter type: ${adapterType}`,
          );
          return;
        }

        logger.log(`📤 Sending adapted event to ${provider.type}:`, adaptedEvent);
        if (Array.isArray(adaptedEvent)) {
          adaptedEvent.forEach((evt) => this.trackOrQueueProviderEvent(provider, evt));
        } else {
          this.trackOrQueueProviderEvent(provider, adaptedEvent);
        }
      });
    } else {
      // 没有 adapterType，使用传统方式处理
      this.trackWithoutAdapter(event as TrackingEvent, providers);
    }
  }

  /**
   * 初始化单个 provider，并用超时保护释放 ReportingCenter 的全局初始化等待。
   * 注意：超时不会取消 provider.init()，后续成功时仍会补发该 provider 队列中的事件。
   */
  private initializeProvider(provider: Provider): Promise<boolean> {
    this.providerInitStatus.set(provider.type, 'initializing');

    // 保留原始 init 链路，timeout 只释放 initialize() 的全局等待。
    // 若 provider 后续加载成功，仍会 flush 它自己的队列。
    const initPromise = provider
      .init()
      .then((result) => {
        if (result && provider.isInitialized()) {
          this.providerInitStatus.set(provider.type, 'ready');
          this.flushProviderQueue(provider);
        } else {
          this.providerInitStatus.set(provider.type, 'failed');
          this.clearProviderQueue(provider.type);
        }

        return result;
      })
      .catch((error) => {
        logger.error(`❌ Provider ${provider.type} initialization failed:`, error);
        this.providerInitStatus.set(provider.type, 'failed');
        this.clearProviderQueue(provider.type);
        return false;
      });

    let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
    const timeoutPromise = new Promise<boolean>((resolve) => {
      timeoutId = globalThis.setTimeout(() => {
        logger.warn(
          `⚠️ Provider ${provider.type} initialization timed out after ${PROVIDER_INIT_TIMEOUT_MS}ms`,
        );
        resolve(false);
      }, PROVIDER_INIT_TIMEOUT_MS);
    });

    return Promise.race([initPromise, timeoutPromise]).finally(() => {
      if (timeoutId) {
        globalThis.clearTimeout(timeoutId);
      }
    });
  }

  /**
   * 启用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时启用所有Provider的日志
   */
  enableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('enableLog' in provider && typeof provider.enableLog === 'function') {
        (provider as any).enableLog();
        logger.log(`✅ Enabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 禁用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时禁用所有Provider的日志
   */
  disableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('disableLog' in provider && typeof provider.disableLog === 'function') {
        (provider as any).disableLog();
        logger.log(`❌ Disabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 获取当前所有Provider的日志状态
   */
  getLoggingStatus(): Record<string, boolean | 'not_supported'> {
    const providers = this.registry.getAllProviders();
    const status: Record<string, boolean | 'not_supported'> = {};

    providers.forEach((provider) => {
      if ('loggingEnabled' in provider) {
        status[provider.type] = (provider as any).loggingEnabled;
      } else {
        status[provider.type] = 'not_supported';
      }
    });

    return status;
  }

  /**
   * 列出所有已注册的Provider
   */
  listProviders(): string[] {
    return this.registry.getAllProviders().map((provider) => provider.type);
  }

  /**
   * 获取特定类型的Provider实例
   */
  getProvider(type: ProviderType): Provider | undefined {
    return this.registry.getProvider(type);
  }

  /**
   * 不使用适配器的传统追踪方式
   */
  private trackWithoutAdapter(event: TrackingEvent, providers: Provider[]): void {
    const sensorsEvent = this.withSensorsEventId(event, providers);
    providers.forEach((provider) => {
      const eventForProvider =
        provider.type === ProviderType.SENSORS || provider.type === ProviderType.SNOWPLOW
          ? sensorsEvent
          : event;

      this.trackOrQueueProviderEvent(provider, eventForProvider);
    });
  }

  /**
   * 当本次目标包含 Sensors 时，为神策及其 Snowplow 镜像事件补齐同一个 eventId。
   */
  private withSensorsEventId(event: TrackingEvent, providers: Provider[]): TrackingEvent {
    const hasSensors = providers.some((provider) => provider.type === ProviderType.SENSORS);
    if (!hasSensors || event.eventId) {
      return event;
    }

    return {
      ...event,
      eventId: generateEventId(),
    };
  }

  private resolveTargetProviders(specificProviders?: ProviderType[]): Provider[] {
    let providers = this.registry.getEnabledProviders();

    if (specificProviders && specificProviders.length > 0) {
      providers = providers.filter((provider) => specificProviders.includes(provider.type));
    }

    return this.withSensorsSnowplowMirror(providers);
  }

  /**
   * 当目标包含 Sensors 时，自动纳入 Snowplow，并保证调用顺序为 Sensors -> Snowplow。
   */
  private withSensorsSnowplowMirror(providers: Provider[]): Provider[] {
    const hasSensors = providers.some((provider) => provider.type === ProviderType.SENSORS);
    const hasSnowplow = providers.some((provider) => provider.type === ProviderType.SNOWPLOW);

    let resolvedProviders = providers;
    if (hasSensors && !hasSnowplow) {
      const snowplowProvider = this.registry.getProvider(ProviderType.SNOWPLOW);
      if (snowplowProvider?.isEnabled) {
        resolvedProviders = [...providers, snowplowProvider];
      }
    }

    return this.orderSensorsSnowplowFirst(resolvedProviders);
  }

  private orderSensorsSnowplowFirst(providers: Provider[]): Provider[] {
    const sensorsProvider = providers.find((provider) => provider.type === ProviderType.SENSORS);
    if (!sensorsProvider) {
      return providers;
    }

    const snowplowProvider = providers.find((provider) => provider.type === ProviderType.SNOWPLOW);
    const remainingProviders = providers.filter(
      (provider) =>
        provider.type !== ProviderType.SENSORS && provider.type !== ProviderType.SNOWPLOW,
    );

    if (!snowplowProvider) {
      return [sensorsProvider, ...remainingProviders];
    }

    return [sensorsProvider, snowplowProvider, ...remainingProviders];
  }

  private trackProviderEvent(provider: Provider, event: TrackingEvent): void {
    if (!provider.isInitialized()) {
      logger.warn(`⚠️ Provider ${provider.type} is not initialized`);
      return;
    }

    try {
      provider.track(event);
    } catch (error) {
      logger.error(`❌ Provider ${provider.type} tracking failed:`, error);
    }
  }

  /**
   * 发送事件到指定 provider；若 provider 仍在初始化，则将事件放入该 provider 的独立队列。
   */
  private trackOrQueueProviderEvent(provider: Provider, event: TrackingEvent): void {
    if (provider.isInitialized()) {
      this.trackProviderEvent(provider, event);
      return;
    }

    const initStatus = this.providerInitStatus.get(provider.type);
    // 确认失败或初始化流程已结束但没有状态的 provider 不再积压事件。
    if (initStatus === 'failed' || (this.initialized && !initStatus)) {
      logger.warn(`⚠️ Provider ${provider.type} is not initialized`);
      return;
    }

    const queue = this.providerEventQueues.get(provider.type) ?? [];
    queue.push(this.cloneEventForQueue(event));
    this.providerEventQueues.set(provider.type, queue);
    logger.warn(`⚠️ Provider ${provider.type} is not initialized, event queued`);
  }

  /**
   * provider 初始化成功后，按入队顺序补发它自己的待发送事件。
   */
  private flushProviderQueue(provider: Provider): void {
    const queue = this.providerEventQueues.get(provider.type);
    if (!queue || queue.length === 0) return;

    // 先删除队列，再逐条发送，避免 provider.track 内部异常或重入造成重复补发。
    this.providerEventQueues.delete(provider.type);
    queue.forEach((event) => this.trackProviderEvent(provider, event));
  }

  /**
   * 清理指定 provider 的待发送队列，主要用于 provider 明确初始化失败的场景。
   */
  private clearProviderQueue(providerType: ProviderType): void {
    this.providerEventQueues.delete(providerType);
  }

  /**
   * 复制入队事件，固定当时的事件内容，避免慢 provider 补发时读到后续被修改的对象。
   */
  private cloneEventForQueue(event: TrackingEvent): TrackingEvent {
    const clonedEvent: TrackingEvent = { ...event };

    // 队列里保存入队时的事件快照，避免慢 provider 补发时读到后续变更的商品/价格上下文。
    if (event.properties !== undefined) {
      clonedEvent.properties = this.cloneQueuePayload(event.properties);
    }

    if (event.options !== undefined) {
      clonedEvent.options = this.cloneQueuePayload(event.options);
    }

    return clonedEvent;
  }

  /**
   * 复制事件 payload；优先使用 structuredClone，不支持或失败时退回 JSON clone。
   */
  private cloneQueuePayload<T>(payload: T): T {
    if (payload === undefined || payload === null) {
      return payload;
    }

    if (typeof globalThis.structuredClone === 'function') {
      try {
        return globalThis.structuredClone(payload);
      } catch {
        // fall through to JSON clone
      }
    }

    try {
      return JSON.parse(JSON.stringify(payload)) as T;
    } catch {
      return payload;
    }
  }

  private processQueue(): void {
    if (this.eventQueue.length === 0) return;

    this.eventQueue.forEach(({ event, providers, adapterType, adaptionData }) =>
      this.track(event, providers, adaptionData, adapterType),
    );
    this.eventQueue = [];
  }

  /**
   * 获取目标Provider列表
   */
  private getTargetProviders(providerTypes?: string[]): Provider[] {
    const allProviders = this.registry.getAllProviders();

    // 如果没有指定类型或传入空数组，返回所有Provider
    if (!providerTypes || providerTypes.length === 0) {
      return allProviders;
    }

    // 过滤指定类型的Provider
    return allProviders.filter((provider) => providerTypes.includes(provider.type));
  }
}
